import {delay} from "redux-saga";
import {call, fork, put, select, take} from "redux-saga/effects";
import {loadScene} from "../../routing/actions";
import {completeLevel} from "../actions";
import {addCoins, incrementCompletedLevels} from "../../player/actions";
import {showBonusAwardDialog} from "../../dialog-manager/dialogs/showBonusAwardDialog";
import {BonusType} from "../../player/types";
import {ILevelMap, ILevelMapTileProgress} from "../types";
import {IQuestionProgress} from "../../questions/types";
import {IApplicationState} from "../..";
import {LevelCompleteStarsConfiguration} from "../../settings/types";

function* calculateLevelStats(level: number) {
    type LevelStatsDependencies = {
        readonly levelMap: ILevelMap;
        readonly levelProgress: Record<number, ILevelMapTileProgress>;
        readonly questionProgress: Record<number, IQuestionProgress>;
        readonly levelCompleteStars: LevelCompleteStarsConfiguration;
    };

    const {levelMap, levelProgress, questionProgress, levelCompleteStars}: LevelStatsDependencies = yield select(({ game, questions, settings }: IApplicationState) => ({
        levelMap: game.levelMaps[level],
        levelProgress: game.levelMapTileProgress,
        questionProgress: questions.progress,
        levelCompleteStars: settings.gameBalance.levelCompleteStars
    }));

    let totalScore = 0;
    let totalQuestions = 0;
    let answeredQuestions = 0;
    let rightAnswers = 0;

    for (let row of levelMap.tiles) {
        for (let tile of row) {
            const tileProgress = levelProgress[tile.id] as ILevelMapTileProgress;

            totalScore += tileProgress?.score || 0;

            if (!tile.questions)
                continue;

            totalQuestions += tile.questions.length;

            if (!tileProgress || !tileProgress.isAnswered)
                continue;

            for (let questionId of tile.questions) {
                const question = questionProgress[tileProgress.overriddenQuestions && tileProgress.overriddenQuestions[questionId] || questionId] as IQuestionProgress;
                if (!question)
                    continue;

                answeredQuestions += 1;

                if (question.isRight) {
                    rightAnswers += 1;
                }
            }
        }
    }

    const completionPercent= totalQuestions > 0 ? answeredQuestions / totalQuestions : 0;
    const rightAnswersPercent = answeredQuestions > 0 ? rightAnswers / answeredQuestions : 0;
    const rightAnswerTotalPercent = answeredQuestions > 0 ? rightAnswers / totalQuestions : 0;

    levelCompleteStars.sort((a, b) => b.count - a.count);

    const stars = levelCompleteStars.find(config => {
        return (!config.minAnsweredQuestions || answeredQuestions > config.minAnsweredQuestions)
            && (!config.rightAnswersPercent || rightAnswerTotalPercent * 100 > config.rightAnswersPercent);
    }) || { count: 0 };

    return {
        id: level,
        answeredQuestions,
        totalQuestions,
        totalScore,
        completionPercent,
        rightAnswersPercent,
        stars: stars.count
    }
}

export function* processLevelComplete(level: number, timeout: number) {
    if (timeout > 0) {
        yield delay(timeout);
    }

    const stats = yield call(calculateLevelStats, level);
    yield put(completeLevel(stats));
    yield put(incrementCompletedLevels());

    yield put(loadScene.request('LevelCompleted'));
    yield take(loadScene.request); // goto level selection

    const {coins} = yield select(({ settings }: IApplicationState) => ({
        coins: settings.gameBalance.coinsForStars[stats.stars] || 0
    }));
    if (coins > 0) {
        yield put(addCoins(coins, "awarded_for_completed_level"));
        yield fork(showBonusAwardDialog, { bonus: BonusType.Coins, count: coins });
    }
}